import { EmptyState } from "@/components/EmptyState";
import { LockIcon } from "@/components/icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Outlet,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import { createContext, use, useState } from "react";

import { CHAT_BUBBLE_MARGIN } from "../lib/limits.ts";

import { BUBBLE_RESERVATION, ChatBubble } from "../chat/ChatBubble.tsx";
import {
  currentPeriod,
  periodChoiceLabel,
  recentPeriods,
  recentYears,
} from "../work/periods.ts";
import { useWorkspace } from "./AppShell.tsx";
import { DashboardSidebar } from "./DashboardSidebar.tsx";
import { useSession } from "./session.tsx";
import { SettingsDialog } from "./SettingsDialog.tsx";
import { SHELL_TEXTS } from "./texts.ts";
import { TopBar } from "./TopBar.tsx";
import {
  canOpenPath,
  CHAT_MODE_ENABLED,
  dashboardTitleOf,
  pathOf,
} from "./workspace.ts";

interface DashboardPeriod {
  readonly period: string;
  readonly setPeriod: (period: string) => void;
}

const PeriodContext = createContext<DashboardPeriod | null>(null);

/**
 * El mes del panel, que es uno solo.
 *
 * Lo elige la cabecera y lo leen todas las secciones. Con un mando por
 * pantalla --uno arriba y otro entre los filtros del padrón-- acaban diciendo
 * meses distintos a la vez y nadie sabe cuál manda.
 *
 * Puede ser un año entero (`2026`) y no un mes: lo entiende el padrón, que
 * filtra por año y mes por separado. Quien necesite un mes de verdad lo
 * pregunta con `isWholeYear` y lo dice; pedir la cobertura de un año a un
 * endpoint mensual es un 422 con peor cara.
 */
export const useDashboardPeriod = (): DashboardPeriod => {
  const value = use(PeriodContext);
  if (value === null) {
    throw new Error(SHELL_TEXTS.dashboardPeriodOutside);
  }
  return value;
};

/**
 * El armazón del panel (§9.2): un panel de control de los de siempre.
 *
 * Sus secciones a la izquierda, contenido al centro, y arriba la misma
 * cabecera que el chat con el conmutador en el mismo sitio. Ni historial ni
 * redactor: aquí no se conversa.
 */
export function DashboardShell() {
  const navigate = useNavigate();
  const { chat, bubble, openInChat } = useWorkspace();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const session = useSession();

  /**
   * El mes viaja en la URL, por la misma razón que la conversación abierta:
   * recargar no lo pierde y el enlace que se comparte enseña lo mismo.
   */
  const chosen = useSearch({ from: "__root__", select: (search) => search.p });
  const period = chosen ?? currentPeriod();

  const setPeriod = (next: string) => {
    void navigate({
      to: ".",
      search: (previous) => ({ ...previous, p: next }),
      replace: true,
    });
  };

  /**
   * Los meses primero y los años enteros al final, separados.
   *
   * El año no es un mes más de la lista: es otra forma de mirar, y va donde no
   * se pulsa por error queriendo pulsar el mes de al lado.
   */
  const months = recentPeriods();
  const years = recentYears(months);
  const known = [...months, ...years];
  const options = known.includes(period)
    ? months
    : /* Lo que venga en la URL y no esté en la lista se añade igualmente: es
         lo que se está mirando, y el selector no puede enseñar otra cosa. */
      [period, ...months];

  return (
    <PeriodContext value={{ period, setPeriod }}>
      <div className="relative flex h-dvh overflow-hidden">
        <DashboardSidebar
          onSettings={() => {
            setSettingsOpen(true);
          }}
        />

        {/* `min-w-0` y `min-h-0`: sin esto una tabla ancha estira el armazón
            y la página entera se mueve en horizontal. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <TopBar
            mode="work"
            onMode={(next) => {
              void navigate({
                to: pathOf(next),
                search: (previous) => previous,
              });
            }}
            context={
              <h1 className="truncate text-base font-semibold">
                {dashboardTitleOf(pathname)}
              </h1>
            }
            actions={
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger
                  className="w-32 font-mono"
                  aria-label={SHELL_TEXTS.periodPicker}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map((month) => (
                    <SelectItem key={month} value={month} className="font-mono">
                      {periodChoiceLabel(month)}
                    </SelectItem>
                  ))}

                  <SelectSeparator />

                  {years.map((year) => (
                    <SelectItem key={year} value={year} className="font-mono">
                      {periodChoiceLabel(year)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />

          {/* Lo único reservado es el pie del botón, y es SIEMPRE el mismo
              esté la ventana abierta o cerrada: así abrir el chat no mueve
              nada de lo que se estaba mirando. Con el chat apagado no hay
              botón, ni hueco. */}
          <div
            className="flex min-h-0 min-w-0 flex-1 flex-col"
            style={CHAT_MODE_ENABLED ? BUBBLE_RESERVATION : undefined}
          >
            {/* Por la URL se llega aunque el menú no la ofrezca: aquí se dice
                por qué no hay nada, en vez de una pantalla de errores 403. */}
            {canOpenPath(pathname, session) ? (
              <Outlet />
            ) : (
              <EmptyState icon={LockIcon} className="my-auto">
                No tienes permiso para ver esta sección. Si crees que deberías,
                pídeselo a quien administra el programa.
              </EmptyState>
            )}
          </div>
        </main>

        {/* Abajo a la derecha y FLOTANDO sobre el contenido.
            La capa es una decisión, no el orden en que quedó el árbol: `z-40`
            la deja por encima del panel --que no declara capa ninguna-- y por
            debajo de los diálogos modales, que son `z-50`. Si se abre la ficha
            de un participante o los ajustes con el chat abierto, manda el
            diálogo. Apagada con el modo Chat (`CHAT_MODE_ENABLED`): vuelve
            al encenderlo. */}
        {CHAT_MODE_ENABLED && (
          <div
            className="absolute right-0 bottom-0 z-40 flex flex-col items-end justify-end"
            style={{ padding: CHAT_BUBBLE_MARGIN, maxHeight: "100%" }}
          >
            <ChatBubble
              chat={chat}
              open={bubble.open}
              onOpenChange={bubble.setOpen}
              onOpenInChat={openInChat}
            />
          </div>
        )}

        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      </div>
    </PeriodContext>
  );
}
